import {FC, useEffect, useState} from 'react'
import {api, CleanerEarning, CleanerTab} from '../../services/api'
import {ACCENT, INK, MUTED, Section, Empty, EarningBadge, fmtDate, money} from './ui'

/** The cleaner's full earnings tab. */
const CleanerEarningsPage: FC = () => {
  const [earnings, setEarnings] = useState<CleanerEarning[]>([])
  const [tab, setTab] = useState<CleanerTab | null>(null)
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(0)

  useEffect(() => {
    Promise.all([api.myCleanerEarnings().catch(() => []), api.cleanerTab().catch(() => null)])
      .then(([e, t]) => {
        setEarnings(e)
        setTab(t)
      })
      .finally(() => setLoading(false))
  }, [])

  const PAGE = 10
  const pages = Math.max(1, Math.ceil(earnings.length / PAGE))
  const cur = Math.min(page, pages - 1)
  const slice = earnings.slice(cur * PAGE, cur * PAGE + PAGE)
  const pagerBtn = (disabled: boolean): React.CSSProperties => ({fontWeight: 700, fontSize: 13, color: disabled ? '#c2c6d2' : INK, background: 'none', border: 'none', cursor: disabled ? 'default' : 'pointer', padding: '4px 8px'})

  return (
    <>
      <div style={{background: 'linear-gradient(135deg, #ffffff, #f3faf5)', borderRadius: 16, boxShadow: '0 6px 18px rgba(27,27,41,0.06)', border: '1px solid #e6f3ea', padding: '20px 22px', marginBottom: 22}}>
        <div style={{fontSize: 12, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: MUTED}}>Owed to you right now</div>
        <div style={{fontSize: 34, fontWeight: 900, color: ACCENT, lineHeight: 1.1, marginTop: 4}}>{money(tab?.owed ?? '0')}</div>
        <div style={{fontSize: 13, color: MUTED, marginTop: 2}}>
          {tab?.owedCount ?? 0} item{(tab?.owedCount ?? 0) === 1 ? '' : 's'} on your tab
          {tab?.pendingCount ? ` · ${tab.pendingCount} awaiting approval` : ''}
        </div>
      </div>

      <Section title='Your earnings' subtitle='Every bed wash and cleaning visit on your tab.'>
        {loading ? (
          <Empty>Loading…</Empty>
        ) : earnings.length === 0 ? (
          <Empty>No earnings yet.</Empty>
        ) : (
          <>
            {slice.map((e) => (
              <div key={e.id} style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '13px 16px', borderTop: '1px solid #f1f3f7'}}>
                <div style={{minWidth: 0}}>
                  <div style={{fontSize: 14.5, fontWeight: 700, color: INK}}>
                    {e.kind === 'BED_WASH' ? `Bed wash${e.bedCode ? ` — Bed ${e.bedCode}` : ''}` : 'Apartment cleaning'}
                  </div>
                  <div style={{fontSize: 12.5, color: MUTED, marginTop: 2}}>
                    {fmtDate(e.occurredOn)}{e.note ? ` · ${e.note}` : ''}
                  </div>
                </div>
                <div style={{textAlign: 'right', whiteSpace: 'nowrap'}}>
                  <div style={{fontSize: 15, fontWeight: 800, color: INK}}>{money(e.amount)}</div>
                  <EarningBadge status={e.status} />
                </div>
              </div>
            ))}
            {pages > 1 && (
              <div style={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderTop: '1px solid #f1f3f7'}}>
                <button disabled={cur === 0} onClick={() => setPage(cur - 1)} style={pagerBtn(cur === 0)}>‹ Prev</button>
                <span style={{fontSize: 13, color: MUTED}}>Page {cur + 1} of {pages}</span>
                <button disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)} style={pagerBtn(cur >= pages - 1)}>Next ›</button>
              </div>
            )}
          </>
        )}
      </Section>
    </>
  )
}

export {CleanerEarningsPage}
